import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import {
  NbButtonModule,
  NbCardModule,
  NbCheckboxModule,
  NbDateService,
  NbDialogRef,
  NbDialogService,
  NbIconModule,
  NbInputModule,
  NbOptionModule,
  NbSelectModule,
  NbSpinnerDirective,
  NbSpinnerModule,
  NbToastrService,
  NbToggleModule
} from '@nebular/theme';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { Observable, Subject, Subscription } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';
import { PostCampaign, PostSaveCampaignComplete, PostSaveCampaignFail } from 'src/app/features/campaigns/redux';
import { PostContent, PostSaveComplete, PostSaveFail } from 'src/app/features/content/redux';
import { PostInsights, SaveInsightComplete, SaveInsightFail } from 'src/app/features/insights/redux';
import { Content, DestinationType } from 'src/app/models/content';
import {
  ACTIVE_PARENT_URI_ID,
  BUNDLE_NAMES,
  CATEGORY_ICON_ID,
  CATEGORY_TITLES,
  CONFIG_REGEX,
  ENVIRONMENTS,
  NOTIFICATION_ICONS,
  PAYLOAD_TYPES,
  PRIME_URIS
} from 'src/app/shared/constants/general-constants';
import { PreviewContentComponent } from 'src/app/shared/ui/preview-content/preview-content.component';
import { DrbState, LoadDrbByVersion, LoadDrbVersions } from '../../drbs/redux';
import { DrbService } from '../../drbs/services/drb.service';
import { prepareCampaignPayload, prepareContentPayload, prepareTriggerPayload } from './wizard.payload-builder';

import { DatePipe, NgFor, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault, TitleCasePipe } from '@angular/common';
import { CodemirrorModule } from '@ctrl/ngx-codemirror';
import { OwlDateTimeModule } from '@danielmoncada/angular-datetime-picker';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import { RouterState } from 'src/app/features/auth/redux';
import { Campaign } from 'src/app/models/campaign';
import { Drb } from 'src/app/models/drb-models/drb';
import { Insight } from 'src/app/models/insight';
import { ENVIRONMENT } from 'src/environments/environment';
import { SharedModule } from '../../../shared/shared.module';
import { CampaignPromotionComponent } from '../../campaigns/campaign-table/view-campaign/campaign-promotion-component/campaign-promotion-component.component';
import { QaTestingComponent } from '../../qa-testing/qa-tool/qa-testing.component';

@Component({
  selector: 'app-wizard-modal',
  templateUrl: './wizard-modal.component.html',
  styleUrls: ['./wizard-modal.component.css'],
  imports: [
    NbCardModule,
    NbSpinnerModule,
    NbIconModule,
    NgIf,
    NgSwitch,
    NgSwitchCase,
    FormsModule,
    ReactiveFormsModule,
    NbInputModule,
    OwlDateTimeModule,
    NbToggleModule,
    CodemirrorModule,
    NbSelectModule,
    NgFor,
    NbOptionModule,
    NgSelectComponent,
    NgOptionComponent,
    NbCheckboxModule,
    CdkDropList,
    CdkDragHandle,
    CdkDrag,
    NbButtonModule,
    QaTestingComponent,
    CampaignPromotionComponent,
    NgSwitchDefault,
    TitleCasePipe,
    DatePipe,
    SharedModule
  ]
})
export class WizardModalComponent implements OnInit, OnDestroy {
  public marketing$: Observable<Drb> = inject(Store).select(DrbState.marketing);
  public marketing_translations$: Observable<Drb> = inject(Store).select(DrbState.marketing_translations);
  public version$: Observable<any> = inject(Store).select(DrbState.versions);
  public username$: Observable<string> = inject(Store).select(RouterState.username);
  private unsubscribe: Subject<void> = new Subject();

  @ViewChild(NbSpinnerDirective) public spinnerDir: NbSpinnerDirective;

  // ─── Public instance fields ─────────────────────────────────────────────────
  @Input() public type = 'default';

  // ─── Init Values ─────────────────────────────────────────────────
  public currentStep = 0;
  public isLoading = false;
  public loadingMessage = '';
  public envName = ENVIRONMENT.name;
  public minStartDate: Date;
  public minEndDate: Date;
  public dateForDatepickerStart: Date;
  public dateForDatepickerEnd: Date;
  public drbIds;
  public createdContent: Content;
  public createdInsight: Insight;
  public createdCampaign: Campaign;
  public username;
  public showErrorMessage = false;
  public filteredTitles = [];
  public startDate = 'startDate';

  public campaignConfiguration: UntypedFormGroup;
  public triggerConfiguration: UntypedFormGroup;
  public contentConfiguration: UntypedFormGroup;

  public readonly destinationTypes = DestinationType;

  public marketing = { version: undefined };
  public marketing_translations = { version: undefined };

  // ─── Constants ─────────────────────────────────────────────────
  public readonly activeParentUriList = ACTIVE_PARENT_URI_ID;
  public readonly primeUriList = PRIME_URIS;
  public readonly categoryTitles = CATEGORY_TITLES;
  public readonly categoryIconId = CATEGORY_ICON_ID;

  // ─── Private instance fields ────────────────────────────────────────────────
  private creationSub?: Subscription;
  private categoryMap: Record<string, { id: string; text: string }> = this.categoryTitles.reduce(
    (dict, cur) => {
      dict[cur.category] = { id: cur.id, text: cur.text };

      return dict;
    },
    {} as Record<string, { id: string; text: string }>
  );

  // ─── Constructor ──────────────────────────────────────────────────────────────
  constructor(
    protected dateService: NbDateService<Date>,
    private store: Store,
    private fb: UntypedFormBuilder,
    private action$: Actions,
    private dialogRef: NbDialogRef<WizardModalComponent>,
    private cdr: ChangeDetectorRef,
    private dialogService: NbDialogService,
    private drbService: DrbService,
    private toastrService: NbToastrService
  ) {
    this.minStartDate = this.dateService.addMonth(this.dateService.today(), 0);
    this.minEndDate = this.dateService.addDay(this.minStartDate, 1);
    this.username$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.username = data));
  }

  // ─── Public property getters ─────────────────────────────────────────────────
  public get payload(): UntypedFormArray {
    const ctrl = this.contentConfiguration.get('payload');

    return ctrl instanceof UntypedFormArray ? ctrl : this.fb.array([]);
  }

  // ─── Lifecycle hooks ─────────────────────────────────────────────────────────
  public ngOnInit(): void {
    this.initCampaignForm();
    this.initTriggerForm();
    this.buildContentForm();
    this.action$
      .pipe(ofActionSuccessful(PostSaveFail, SaveInsightFail, PostSaveCampaignFail), takeUntil(this.unsubscribe))
      .subscribe((action) => {
        console.error(action);
        this.isLoading = false;
        this.loadingMessage = $localize`Something went wrong. Please try again.`;
        this.showErrorMessage = true;
      });
    if (ENVIRONMENT.name !== ENVIRONMENTS.TKSTAGE && ENVIRONMENT.name !== ENVIRONMENTS.TKPROD) {
      for (const key in BUNDLE_NAMES) {
        const bundle_name = key.toLowerCase();
        if (this.store.selectSnapshot(DrbState.versions)[bundle_name].length === 0) {
          this.store.dispatch(new LoadDrbVersions(bundle_name));
        }
      }
      this.version$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
        for (const key in BUNDLE_NAMES) {
          const bundle_name = key.toLowerCase();
          if (data[bundle_name].length > 0) {
            const versions = [...data[bundle_name]].reverse();
            if (!this.store.selectSnapshot(DrbState.getByKey(bundle_name)).version) {
              this.store.dispatch(new LoadDrbByVersion(versions[0], bundle_name));
            }
          }
        }
      });
      this.marketing$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
        this.marketing = data;
        this.drbIds = this.drbService.getDrbIds();
      });
      this.marketing_translations$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
        this.marketing_translations = data;
        this.drbIds = this.drbService.getDrbIds();
      });
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.creationSub?.unsubscribe();
  }

  // ─── Public instance methods ─────────────────────────────────────────────────

  public addTag(event) {
    return;
  }

  /** CDK drag-and-drop */
  public drop(event: CdkDragDrop<any[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    moveItemInArray(this.payload.controls, event.previousIndex, event.currentIndex);
    this.payload.updateValueAndValidity();
  }

  /** Wizard nav */
  public next(): void {
    const form = this.getCurrentForm();
    if (!form.valid) {
      form.markAllAsTouched();

      return;
    }

    // If we're on Recap (step 3), start the multi-step creation flow:
    if (this.currentStep === 3) {
      this.startCreation();
    } else if (this.currentStep < 6) {
      this.currentStep++;
    } else if (this.currentStep === 6) {
      this.close();
    }
  }

  public previous(): void {
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

  public getStepTitle(): string {
    const titles = [
      $localize`General Configuration`,
      $localize`Trigger Configuration`,
      $localize`Content Configuration`,
      $localize`Recap and Create`,
      $localize`QA`,
      $localize`Promote`
    ];

    return titles[this.currentStep] || '';
  }

  public trackByIndex(index: number): number {
    return index;
  }

  public onSelectionChange(icon) {
    // need to do this to update the title list after icon change
    this.filteredTitles = this.categoryTitles.filter((c) => c.category === icon || c.category === 'all');

    // force re-render
    this.cdr.detectChanges();

    // 3)  wait a tick, then patch
    setTimeout(() => {
      const pick = this.categoryMap[icon] || this.categoryMap.all;
      this.contentConfiguration.patchValue(
        {
          category_title_id: pick.id,
          category_title_text: pick.text
        },
        { emitEvent: false }
      );
    }, 0);
  }

  public onCategoryTitleSelect(text: string): void {
    const cat = this.categoryTitles.find((c) => c.text === text);
    if (cat) {
      this.contentConfiguration.get('category_title_id')?.setValue(cat.id);
    }
  }

  public trackByPayloadKey(index: number, ctrl: AbstractControl): string {
    return ctrl.get('key')?.value ?? index.toString();
  }

  public changeEvent() {
    this.compareStartEndDate();
  }

  public close(): void {
    this.dialogRef.close();
  }

  public getCurrentForm(): UntypedFormGroup {
    if (this.currentStep === 0) {
      return this.campaignConfiguration;
    }
    if (this.currentStep === 1) {
      return this.triggerConfiguration;
    }
    if (this.currentStep === 2) {
      return this.contentConfiguration;
    }

    return this.campaignConfiguration;
  }

  public toggleActionValidators(enabled: boolean): void {
    // update validation on action button enable/disable
    const btn = this.contentConfiguration.get('text_id');
    const uri = this.contentConfiguration.get('destination_uri_id');
    const dtype = this.contentConfiguration.get('destination_type');

    const btn_non_local = this.contentConfiguration.get('text');
    const uri_non_local = this.contentConfiguration.get('destination_uri');

    if (enabled) {
      if (this.campaignConfiguration.get('non_localization_campaign').value) {
        btn_non_local?.setValidators(Validators.required);
        uri_non_local?.setValidators(Validators.required);
        dtype?.setValidators(Validators.required);
      } else {
        btn?.setValidators(Validators.required);
        uri?.setValidators(Validators.required);
        dtype?.setValidators(Validators.required);
      }
    } else {
      btn?.clearValidators();
      btn_non_local?.clearValidators();
      uri_non_local?.clearValidators();
      uri?.clearValidators();
      dtype?.clearValidators();
    }

    btn?.updateValueAndValidity();
    uri?.updateValueAndValidity();
    dtype?.updateValueAndValidity();
  }

  // ─── Private helpers ─────────────────────────────────────────────────────────
  private initCampaignForm(): void {
    this.campaignConfiguration = this.fb.group({
      campaignName: ['', [Validators.required, Validators.pattern(new RegExp(CONFIG_REGEX, 'i'))]],
      campaignDescription: [''],
      prime: [false],
      non_localization_campaign: [false]
    });
    if (this.envName !== ENVIRONMENTS.STAGE) {
      this.campaignConfiguration.addControl(this.startDate, this.fb.control(null, Validators.required));
      this.campaignConfiguration.addControl('endDate', this.fb.control(null, Validators.required));
    }

    const startCtrl = this.campaignConfiguration.get(this.startDate);
    if (startCtrl) {
      startCtrl.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((start: Date) => this.updateEndDateMin(start));
    }
  }

  private compareStartEndDate() {
    if (this.dateForDatepickerEnd) {
      if (this.campaignConfiguration.get(this.startDate).value.getTime() > this.dateForDatepickerEnd.getTime()) {
        this.dateForDatepickerEnd = this.dateService.addDay(
          new Date(this.campaignConfiguration.get(this.startDate).value.getTime()),
          1
        );
        this.minEndDate = this.dateService.addDay(
          new Date(this.campaignConfiguration.get(this.startDate).value.getTime()),
          1
        );
      } else {
        this.minEndDate = this.dateService.addDay(
          new Date(this.campaignConfiguration.get(this.startDate).value.getTime()),
          1
        );
      }
    } else {
      this.minEndDate = this.dateService.addDay(
        new Date(this.campaignConfiguration.get(this.startDate).value.getTime()),
        1
      );
    }
  }

  private updateEndDateMin(start: Date): void {
    if (!(start instanceof Date) || isNaN(start.getTime())) {
      return;
    }
    const minEnd = this.dateService.addDay(start, 1);
    this.minEndDate = minEnd;

    const endCtrl = this.campaignConfiguration.get('endDate');
    if (!endCtrl) {
      return;
    }
    const currentEnd = endCtrl.value as Date;
    if (!(currentEnd instanceof Date) || currentEnd <= start) {
      endCtrl.setValue(minEnd);
    }
  }

  private initTriggerForm(): void {
    this.triggerConfiguration = this.fb.group({
      query: ['']
    });
  }

  private buildContentForm(): void {
    this.contentConfiguration = this.fb.group({
      category_icon_id: ['', Validators.required],
      category_title_id: ['', Validators.required],
      category_title_text: ['', Validators.required],
      headline_text_id: ['', Validators.required],
      headline_text: [''],
      action_button: [false],
      text_id: [''],
      text: [''],
      destination_uri: [''],
      destination_uri_id: [''],
      destination_type: [''],
      active_parent_uri_id: [''],
      payload: this.fb.array([])
    });
    const actCtrl = this.contentConfiguration.get('action_button');
    if (actCtrl) {
      actCtrl.valueChanges
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((enabled) => this.toggleActionValidators(enabled));
    }

    // Init as localization campaign
    this.payload.push(this.createPayloadItem('Content Image ID'));
    this.payload.push(this.createPayloadItem('Content Text ID'));
    this.payload.push(this.createPayloadItem('Content HTML ID'));
    const localizationCtrl = this.campaignConfiguration.get('non_localization_campaign');

    if (localizationCtrl) {
      localizationCtrl.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
        const headline_text = this.contentConfiguration.get('headline_text');
        const headline_text_id = this.contentConfiguration.get('headline_text_id');
        if (localizationCtrl.value) {
          headline_text_id.clearValidators();
          headline_text.setValidators(Validators.required);
          this.payload.clear();
          this.payload.push(this.createPayloadItem('Content Image URL'));
          this.payload.push(this.createPayloadItem('Content Text'));
          this.payload.push(this.createPayloadItem('Content HTML'));
        } else {
          headline_text_id.setValidators(Validators.required);
          headline_text.clearValidators();
          this.payload.clear();
          this.payload.push(this.createPayloadItem('Content Image ID'));
          this.payload.push(this.createPayloadItem('Content Text ID'));
          this.payload.push(this.createPayloadItem('Content HTML ID'));
        }
      });
    }

    if (!this.contentConfiguration.get('payload')) {
      this.contentConfiguration.addControl('payload', this.fb.array([]));
    }
    if (this.type === 'so') {
      // for special offers we can set the icon
      this.contentConfiguration.get('category_icon_id').setValue(NOTIFICATION_ICONS.SPECIAL_OFFER);
      this.onSelectionChange(NOTIFICATION_ICONS.SPECIAL_OFFER);
    }
  }

  private createPayloadItem(key: string): UntypedFormGroup {
    return this.fb.group({ key: [key], id: [''] });
  }

  private showStep(msg: string) {
    this.loadingMessage = msg;
    // if spinner already shown, hide & show to re-inject the message
    if (this.spinnerDir.isSpinnerExist) {
      this.spinnerDir.hide();
      this.spinnerDir.show();
    } else {
      this.isLoading = true;
    }
  }
  /** Kick off content → trigger → campaign */
  private startCreation(): void {
    if (this.creationSub) {
      this.creationSub.unsubscribe();
    }
    this.isLoading = true;

    const contentData = prepareContentPayload(
      this.contentConfiguration,
      this.campaignConfiguration,
      this.username,
      this.type
    );
    const triggerData = prepareTriggerPayload(
      this.campaignConfiguration,
      this.triggerConfiguration,
      this.username,
      this.dateService
    );

    // 1) Create Content
    this.showStep($localize`Creating content…`);
    this.store.dispatch(new PostContent(contentData));

    // 2) Wait for Content Success
    this.creationSub = this.action$
      .pipe(
        ofActionSuccessful(PostSaveComplete),
        take(1),
        switchMap((contentAction: PostSaveComplete) => {
          const contentId = contentAction.data.content_id;
          this.createdContent = Object.assign(new Content(), contentAction.data);

          this.showStep($localize`Creating trigger…`);
          // 3) Create Trigger
          this.store.dispatch(new PostInsights(triggerData));

          return this.action$.pipe(
            ofActionSuccessful(SaveInsightComplete),
            take(1),
            map((insightAction: SaveInsightComplete) => {
              this.createdInsight = Object.assign(new Insight(), insightAction.data);

              return {
                contentId,
                triggerId: insightAction.data.id
              };
            }),
            takeUntil(this.unsubscribe)
          );
        }),
        // 4) Create Campaign when both IDs are available
        switchMap(({ contentId, triggerId }) => {
          this.showStep($localize`Creating campaign…`);

          const campaignData = prepareCampaignPayload(
            contentId,
            triggerId,
            this.campaignConfiguration,
            this.type,
            this.username
          );
          this.store.dispatch(new PostCampaign(campaignData));

          return this.action$.pipe(
            ofActionSuccessful(PostSaveCampaignComplete),
            take(1),
            map((campaignAction: PostSaveCampaignComplete) => {
              this.createdCampaign = Object.assign(new Campaign(), campaignAction.campaign);

              return;
            }),
            takeUntil(this.unsubscribe)
          );
        })
      )
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.showErrorMessage = false;
          this.currentStep++;
        },
        error: (err) => {
          console.error('Creation workflow failed', err);
          this.isLoading = false;
          this.loadingMessage = $localize`An error occurred.`;
        }
      });
  }

  public previewContent() {
    const content = prepareContentPayload(
      this.contentConfiguration,
      this.campaignConfiguration,
      this.username,
      this.type
    );
    const drbIds = PreviewContentComponent.getDrbIds(content);
    if (drbIds.length === 0) {
      this.dialogService.open(PreviewContentComponent, {
        closeOnEsc: true,
        closeOnBackdropClick: true,
        context: {
          content: content,
          values: drbIds,
          isSpecialOffer: content.payload_records[0]?.payload_record_type === PAYLOAD_TYPES.PERSONALIZED_OFFER
        }
      });
    } else {
      const data = this.drbService.getDrbValues(drbIds);
      if (data != null) {
        this.dialogService.open(PreviewContentComponent, {
          closeOnEsc: true,
          closeOnBackdropClick: true,
          context: {
            content: content,
            values: data,
            isSpecialOffer: content.payload_records[0]?.payload_record_type === PAYLOAD_TYPES.PERSONALIZED_OFFER
          }
        });
      }
    }
  }
}
